import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft,
  Loader2,
  Save,
  Briefcase,
  Globe,
  Users,
  Shield,
  Palette,
  MessageSquare,
  Lock,
  Database,
  Upload,
  X,
  Check,
  Search,
  Bot,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { getEmojiFromCode } from "./Settings";
import { cn } from "@/lib/utils";

const PREDEFINED_AVATARS = [
  { code: "emoji:robot", emoji: "🤖", label: "Robô" },
  { code: "emoji:scales", emoji: "⚖️", label: "Balança" },
  { code: "emoji:books", emoji: "📚", label: "Livros" },
  { code: "emoji:gavel", emoji: "🔨", label: "Martelo" },
  { code: "emoji:shield", emoji: "🛡️", label: "Escudo" },
  { code: "emoji:owl", emoji: "🦉", label: "Coruja" },
  { code: "emoji:bulb", emoji: "💡", label: "Ideia" },
  { code: "emoji:brain", emoji: "🧠", label: "Cérebro" },
  { code: "emoji:star", emoji: "⭐", label: "Estrela" },
  { code: "emoji:sparkles", emoji: "✨", label: "Brilho" },
];

const RESPONSE_TONES = [
  {
    value: "professional" as const,
    label: "Profissional",
    emoji: "💼",
    description: "Objetivo e direto, adequado para ambiente de trabalho",
  },
  {
    value: "friendly" as const,
    label: "Amigável",
    emoji: "😊",
    description: "Acolhedor e prestativo, como um colega de trabalho",
  },
  {
    value: "formal" as const,
    label: "Formal",
    emoji: "📜",
    description: "Extremamente formal, linguagem técnica jurídica",
  },
  {
    value: "casual" as const,
    label: "Informal",
    emoji: "💬",
    description: "Descontraído e casual, como um amigo próximo",
  },
];

interface Caso {
  id: string;
  title: string;
  description?: string | null;
}

interface ScopeItem {
  id: string;
  resource_type: string;
  resource_id: string;
}

interface SelectableResource {
  id: string;
  title: string;
  type: "CASO" | "LEGAL_CHAT";
  description?: string;
}

export default function AgentConfig() {
  const { agentId } = useParams<{ agentId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Agent fields
  const [name, setName] = useState("Novo Assessor");
  const [avatar, setAvatar] = useState<string | null>(null);
  const [agentType, setAgentType] = useState("geral");
  const [tone, setTone] = useState("professional");
  const [scopeMode, setScopeMode] = useState("ALL");
  const [externalKnowledge, setExternalKnowledge] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [linkedCasoId, setLinkedCasoId] = useState<string | null>(null);

  // Resources for scope selection
  const [casos, setCasos] = useState<Caso[]>([]);
  const [resources, setResources] = useState<SelectableResource[]>([]);
  const [scopeItems, setScopeItems] = useState<ScopeItem[]>([]);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (user && agentId) {
      loadAgent();
      loadResources();
    }
  }, [user, agentId]);

  const loadAgent = async () => {
    try {
      const { data, error } = await supabase
        .from("ai_agents")
        .select("*")
        .eq("id", agentId)
        .eq("user_id", user?.id)
        .single();
      if (error) throw error;
      if (!data) {
        toast.error("Assessor não encontrado");
        navigate("/assessor");
        return;
      }
      const d = data as any;
      setName(d.agent_name);
      setAvatar(d.agent_avatar);
      setAgentType(d.agent_type);
      setTone(d.response_tone || "professional");
      setScopeMode(d.scope_mode || "ALL");
      setExternalKnowledge(d.external_knowledge_allowed || false);
      setIsActive(d.is_active);
      setLinkedCasoId(d.linked_caso_id);

      // Load scope items for this agent
      const { data: scopeData } = await supabase
        .from("ai_agent_scope_items" as any)
        .select("*")
        .eq("agent_id", agentId)
        .eq("user_id", user?.id);
      setScopeItems((scopeData as any[]) || []);
    } catch (err) {
      console.error(err);
      toast.error("Erro ao carregar assessor");
      navigate("/assessor");
    } finally {
      setIsLoading(false);
    }
  };

  const loadResources = async () => {
    try {
      const [casosRes, chatsRes] = await Promise.all([
        supabase
          .from("casos")
          .select("id, title, description")
          .eq("user_id", user?.id)
          .order("updated_at", { ascending: false }),
        supabase
          .from("legal_conversations")
          .select("id, title")
          .eq("user_id", user?.id)
          .is("deleted_at", null)
          .order("updated_at", { ascending: false }),
      ]);

      const casosList = (casosRes.data || []) as Caso[];
      setCasos(casosList);

      const allResources: SelectableResource[] = [
        ...casosList.map((c) => ({
          id: c.id,
          title: c.title,
          type: "CASO" as const,
          description: c.description || undefined,
        })),
        ...(chatsRes.data || []).map((c: any) => ({
          id: c.id,
          title: c.title || "Chat sem título",
          type: "LEGAL_CHAT" as const,
        })),
      ];
      setResources(allResources);
    } catch {}
  };

  const isResourceSelected = (resourceId: string, resourceType: string) => {
    return scopeItems.some(
      (item) => item.resource_id === resourceId && item.resource_type === resourceType
    );
  };

  const handleToggleResource = async (resource: SelectableResource) => {
    if (!user || !agentId) return;
    const isSelected = isResourceSelected(resource.id, resource.type);

    try {
      if (isSelected) {
        await supabase
          .from("ai_agent_scope_items" as any)
          .delete()
          .eq("agent_id", agentId)
          .eq("user_id", user.id)
          .eq("resource_id", resource.id)
          .eq("resource_type", resource.type);

        setScopeItems((prev) =>
          prev.filter(
            (item) => !(item.resource_id === resource.id && item.resource_type === resource.type)
          )
        );
      } else {
        const { data, error } = await supabase
          .from("ai_agent_scope_items" as any)
          .insert({
            agent_id: agentId,
            user_id: user.id,
            resource_id: resource.id,
            resource_type: resource.type,
          })
          .select()
          .single();

        if (error) throw error;
        setScopeItems((prev) => [...prev, data as any]);
      }
    } catch (error) {
      console.error("Error toggling resource:", error);
      toast.error("Erro ao atualizar seleção");
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const { error } = await supabase
        .from("ai_agents")
        .update({
          agent_name: name,
          agent_avatar: avatar,
          agent_type: agentType,
          response_tone: tone,
          scope_mode: scopeMode,
          external_knowledge_allowed: externalKnowledge,
          is_active: isActive,
          linked_caso_id: agentType === "caso" ? linkedCasoId : null,
        })
        .eq("id", agentId);
      if (error) throw error;
      toast.success("Assessor salvo com sucesso!");
    } catch (err) {
      console.error(err);
      toast.error("Erro ao salvar assessor");
    } finally {
      setIsSaving(false);
    }
  };

  const filteredResources = resources.filter((r) =>
    r.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const casosCount = resources.filter((r) => r.type === "CASO").length;
  const chatsCount = resources.filter((r) => r.type === "LEGAL_CHAT").length;
  const selectedCount = scopeItems.length;

  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto p-6 md:p-8 space-y-6 max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate("/assessor")}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <Bot className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Configurar Assessor</h1>
            <p className="text-muted-foreground">Configure como o Assessor acessa seus dados</p>
          </div>
        </div>
      </div>

      {/* Status toggle */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Ativar Assessor
          </CardTitle>
          <CardDescription>Habilite o assessor para uso nos chats</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Status do Assessor</Label>
              <p className="text-sm text-muted-foreground">
                {isActive
                  ? "O assessor está ativo e disponível para uso"
                  : "O assessor está inativo e não aparecerá nos chats"}
              </p>
            </div>
            <Switch checked={isActive} onCheckedChange={setIsActive} />
          </div>
        </CardContent>
      </Card>

      {/* Personalização */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Palette className="h-5 w-5" />
            Personalização
          </CardTitle>
          <CardDescription>Personalize a aparência do seu assessor</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Avatar Section */}
          <div className="space-y-4">
            <Label>Avatar do Assessor</Label>

            {/* Preview */}
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden border-2 border-primary/30">
                {avatar?.startsWith("emoji:") ? (
                  <span className="text-3xl">{getEmojiFromCode(avatar)}</span>
                ) : avatar ? (
                  <img src={avatar} alt="Avatar" className="w-full h-full object-cover" />
                ) : (
                  <Bot className="h-8 w-8 text-muted-foreground" />
                )}
              </div>
              <div className="text-sm text-muted-foreground">
                {avatar?.startsWith("emoji:")
                  ? "Avatar pré-definido selecionado"
                  : avatar
                  ? "Imagem personalizada"
                  : "Nenhum avatar selecionado"}
              </div>
            </div>

            {/* Predefined avatars */}
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Escolha um avatar:</Label>
              <div className="grid grid-cols-5 sm:grid-cols-10 gap-2">
                {PREDEFINED_AVATARS.map((a) => (
                  <button
                    key={a.code}
                    type="button"
                    onClick={() => setAvatar(a.code)}
                    className={cn(
                      "w-10 h-10 rounded-lg flex items-center justify-center text-xl transition-all border-2 relative",
                      "hover:scale-110 hover:bg-accent",
                      avatar === a.code
                        ? "border-primary bg-primary/10 ring-2 ring-primary/20"
                        : "border-transparent bg-muted"
                    )}
                    title={a.label}
                  >
                    {a.emoji}
                    {avatar === a.code && (
                      <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-primary flex items-center justify-center">
                        <Check className="h-2.5 w-2.5 text-primary-foreground" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Divider */}
            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-border" />
              <span className="text-xs text-muted-foreground">ou</span>
              <div className="flex-1 h-px bg-border" />
            </div>

            {/* Custom upload */}
            <div className="flex items-center gap-3">
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      const reader = new FileReader();
                      reader.onloadend = () => {
                        setAvatar(reader.result as string);
                      };
                      reader.readAsDataURL(file);
                    }
                  }}
                />
                <div className="flex items-center gap-2 px-3 py-2 bg-secondary hover:bg-secondary/80 rounded-md text-sm transition-colors">
                  <Upload className="h-4 w-4" />
                  Enviar imagem personalizada
                </div>
              </label>

              {avatar && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setAvatar(null)}
                  className="text-destructive hover:text-destructive"
                >
                  <X className="h-4 w-4 mr-1" />
                  Remover
                </Button>
              )}
            </div>
          </div>

          {/* Name */}
          <div className="space-y-2">
            <Label>Nome do Assessor</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value.slice(0, 50))}
              placeholder="Ex: Meu Assessor Jurídico"
              maxLength={50}
            />
            <p className="text-xs text-muted-foreground">{name.length}/50 caracteres</p>
          </div>
        </CardContent>
      </Card>

      {/* Tipo do Assessor */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Briefcase className="h-5 w-5" />
            Tipo do Assessor
          </CardTitle>
          <CardDescription>Defina o contexto de atuação do assessor</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <RadioGroup value={agentType} onValueChange={setAgentType} className="space-y-3">
            <div
              className={cn(
                "flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all",
                agentType === "caso" ? "border-primary bg-primary/5" : "border-transparent hover:bg-muted"
              )}
              onClick={() => setAgentType("caso")}
            >
              <RadioGroupItem value="caso" id="tipo-caso" className="mt-1" />
              <div className="flex-1">
                <label htmlFor="tipo-caso" className="flex items-center gap-2 font-medium cursor-pointer">
                  <Briefcase className="h-4 w-4 text-primary" />
                  Caso
                </label>
                <p className="text-sm text-muted-foreground mt-0.5">
                  Vinculado a um caso específico. Acessa documentos e dados daquele caso.
                </p>
              </div>
            </div>
            <div
              className={cn(
                "flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all",
                agentType === "geral" ? "border-primary bg-primary/5" : "border-transparent hover:bg-muted"
              )}
              onClick={() => setAgentType("geral")}
            >
              <RadioGroupItem value="geral" id="tipo-geral" className="mt-1" />
              <div className="flex-1">
                <label htmlFor="tipo-geral" className="flex items-center gap-2 font-medium cursor-pointer">
                  <Globe className="h-4 w-4 text-primary" />
                  Geral
                </label>
                <p className="text-sm text-muted-foreground mt-0.5">
                  Assistente de uso geral. Pode acessar todos os seus dados ou itens selecionados.
                </p>
              </div>
            </div>
            <div
              className={cn(
                "flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all",
                agentType === "compartilhado" ? "border-primary bg-primary/5" : "border-transparent hover:bg-muted"
              )}
              onClick={() => setAgentType("compartilhado")}
            >
              <RadioGroupItem value="compartilhado" id="tipo-compartilhado" className="mt-1" />
              <div className="flex-1">
                <label htmlFor="tipo-compartilhado" className="flex items-center gap-2 font-medium cursor-pointer">
                  <Users className="h-4 w-4 text-primary" />
                  Compartilhado
                </label>
                <p className="text-sm text-muted-foreground mt-0.5">
                  Para trabalhos em equipe. Acessa dados compartilhados com sua firma ou grupo.
                </p>
              </div>
            </div>
          </RadioGroup>

          {agentType === "caso" && (
            <div className="mt-4 space-y-2">
              <Label>Caso vinculado</Label>
              <Select value={linkedCasoId || ""} onValueChange={(v) => setLinkedCasoId(v || null)}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um caso" />
                </SelectTrigger>
                <SelectContent>
                  {casos.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tom de Resposta */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Tom de Resposta
          </CardTitle>
          <CardDescription>Como o assessor deve se comunicar com você</CardDescription>
        </CardHeader>
        <CardContent>
          <RadioGroup value={tone} onValueChange={setTone} className="space-y-3">
            {RESPONSE_TONES.map((t) => (
              <div
                key={t.value}
                className={cn(
                  "flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all",
                  tone === t.value
                    ? "border-primary bg-primary/5"
                    : "border-transparent hover:bg-muted"
                )}
                onClick={() => setTone(t.value)}
              >
                <RadioGroupItem value={t.value} id={`tone-${t.value}`} className="mt-1" />
                <div className="flex-1">
                  <label
                    htmlFor={`tone-${t.value}`}
                    className="flex items-center gap-2 font-medium cursor-pointer"
                  >
                    <span className="text-lg">{t.emoji}</span>
                    {t.label}
                  </label>
                  <p className="text-sm text-muted-foreground mt-0.5">{t.description}</p>
                </div>
              </div>
            ))}
          </RadioGroup>
        </CardContent>
      </Card>

      {/* Conhecimento Externo */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {externalKnowledge ? <Globe className="h-5 w-5" /> : <Lock className="h-5 w-5" />}
            Conhecimento Externo
          </CardTitle>
          <CardDescription>
            Permite que o assessor use conhecimento jurídico geral além dos seus dados
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Permitir conhecimento externo</Label>
              <p className="text-sm text-muted-foreground">
                {externalKnowledge
                  ? "O Assessor pode complementar com conhecimento jurídico geral"
                  : "O Assessor responde apenas com seus dados internos"}
              </p>
            </div>
            <Switch checked={externalKnowledge} onCheckedChange={setExternalKnowledge} />
          </div>
        </CardContent>
      </Card>

      {/* Escopo de Acesso */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            Escopo de Acesso
          </CardTitle>
          <CardDescription>Defina quais dados o Assessor pode consultar</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <RadioGroup value={scopeMode} onValueChange={setScopeMode} className="space-y-3">
            <div className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-accent/50 cursor-pointer">
              <RadioGroupItem value="ALL" id="scope-all" />
              <Label htmlFor="scope-all" className="flex-1 cursor-pointer">
                <div className="font-medium">Acessar tudo da minha conta</div>
                <div className="text-sm text-muted-foreground">
                  {casosCount} casos e {chatsCount} conversas disponíveis
                </div>
              </Label>
            </div>
            <div className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-accent/50 cursor-pointer">
              <RadioGroupItem value="SELECTED" id="scope-selected" />
              <Label htmlFor="scope-selected" className="flex-1 cursor-pointer">
                <div className="font-medium">Selecionar casos e conversas específicas</div>
                <div className="text-sm text-muted-foreground">
                  {selectedCount} item(s) selecionado(s)
                </div>
              </Label>
            </div>
          </RadioGroup>

          {scopeMode === "SELECTED" && (
            <div className="space-y-4 pt-4 border-t">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar casos e conversas..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>

              <ScrollArea className="h-[300px] rounded-lg border">
                <div className="p-2 space-y-1">
                  {filteredResources.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">
                      Nenhum recurso encontrado
                    </p>
                  ) : (
                    filteredResources.map((resource) => {
                      const isSelected = isResourceSelected(resource.id, resource.type);
                      return (
                        <button
                          key={`${resource.type}-${resource.id}`}
                          onClick={() => handleToggleResource(resource)}
                          className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-accent/50 transition-colors text-left"
                        >
                          <Checkbox checked={isSelected} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              {resource.type === "CASO" ? (
                                <Briefcase className="h-4 w-4 text-muted-foreground shrink-0" />
                              ) : (
                                <MessageSquare className="h-4 w-4 text-muted-foreground shrink-0" />
                              )}
                              <span className="font-medium truncate">{resource.title}</span>
                              <Badge variant="outline" className="shrink-0">
                                {resource.type === "CASO" ? "Caso" : "Chat"}
                              </Badge>
                            </div>
                            {resource.description && (
                              <p className="text-sm text-muted-foreground truncate mt-0.5">
                                {resource.description}
                              </p>
                            )}
                          </div>
                          {isSelected && <Check className="h-4 w-4 text-primary shrink-0" />}
                        </button>
                      );
                    })
                  )}
                </div>
              </ScrollArea>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Save */}
      <div className="flex justify-end gap-3 pb-6">
        <Button variant="outline" onClick={() => navigate("/assessor")}>
          Voltar
        </Button>
        <Button onClick={handleSave} disabled={isSaving}>
          {isSaving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Salvando...
            </>
          ) : (
            <>
              <Check className="mr-2 h-4 w-4" />
              Salvar Assessor
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
